/**
 * FindingCard -- Displays an open or resolved finding with actions
 *
 * Expandable card showing severity, status, title, and actions.
 * Click to expand/collapse observation and recommendation.
 * When isEditing is true, renders FindingInlineEditor instead of the normal card.
 */

import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import type { AuditDetailTab } from "@/packages/types";
import { Button, ConfirmDialog } from "@/components/ui";
import {
  useResolveFinding,
  useReopenFinding,
  useDeleteFinding,
} from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";
import {
  useSeverityConfig,
  useStatusConfig,
} from "../../findings/findings-constants";
import type { FindingStatusType } from "@/packages/domains/findings";
import { FindingInlineEditor } from "./FindingInlineEditor";

interface ConfirmedFinding {
  _id: Id<"findings">;
  title: string;
  observation: string;
  recommendation: string;
  severity: "critical" | "major" | "minor" | "observation";
  status: FindingStatusType;
}

interface FindingCardProps {
  finding: ConfirmedFinding;
  isEditing: boolean;
  onEdit: () => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  /** When true, show a link to the source checklist item */
  showSourceLink?: boolean;
  /** Checklist item ID for the source link */
  checklistItemId?: Id<"checklistItems">;
}

export function FindingCard({
  finding,
  isEditing,
  onEdit,
  onEditSave,
  onEditCancel,
  showSourceLink = false,
  checklistItemId,
}: FindingCardProps) {
  const { t } = useTranslation("audits");
  const [expanded, setExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate({ from: "/$workspaceSlug/audits/$auditId" });
  const resolveMutation = useResolveFinding();
  const reopenMutation = useReopenFinding();
  const deleteMutation = useDeleteFinding();
  const severityConfig = useSeverityConfig();
  const statusConfig = useStatusConfig();
  const colors = severityConfig[finding.severity];
  const statusStyle = statusConfig[finding.status] ?? statusConfig.open;

  function handleDelete() {
    deleteMutation.mutateAsync({ id: finding._id });
    setShowDeleteConfirm(false);
  }

  return (
    <>
      <FindingInlineEditor
        finding={finding}
        isEditing={isEditing}
        onSave={onEditSave}
        onCancel={onEditCancel}
      />
      <div
        className="rounded-md border p-3 space-y-2"
        style={{ borderColor: colors.border, backgroundColor: colors.bg }}
      >
        <button
          type="button"
          className="flex items-center gap-2 w-full text-left cursor-pointer"
          onClick={() => setExpanded((prev) => !prev)}
        >
          <span
            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: colors.dot }}
          />
          <span
            className="text-xs font-medium px-1.5 py-0.5 rounded uppercase"
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.text,
            }}
          >
            {statusStyle.label}
          </span>
          <span className="font-medium text-sm flex-1">{finding.title}</span>
          {expanded ? (
            <ChevronDown size={14} className="text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight
              size={14}
              className="text-muted-foreground shrink-0"
            />
          )}
        </button>

        {expanded && (
          <>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">
                {t("findings.card.observation")}{" "}
              </span>
              {finding.observation}
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">
                {t("findings.card.recommendation")}{" "}
              </span>
              {finding.recommendation}
            </p>
          </>
        )}

        {showSourceLink && checklistItemId && (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:underline flex items-center gap-1 cursor-pointer"
            onClick={() => {
              void navigate({
                search: {
                  tab: "checklist" as AuditDetailTab,
                  checklistItemId,
                },
              });
            }}
          >
            {t("findings.card.viewSourceChecklistItem")}
          </button>
        )}

        <div className="flex gap-2 pt-1">
          {finding.status === "open" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => resolveMutation.mutateAsync({ id: finding._id })}
              disabled={resolveMutation.isPending}
            >
              {t("findings.card.resolve")}
            </Button>
          )}
          {finding.status === "resolved" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => reopenMutation.mutateAsync({ id: finding._id })}
              disabled={reopenMutation.isPending}
            >
              {t("findings.card.reopen")}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onEdit}>
            {t("findings.card.edit")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteMutation.isPending}
          >
            {t("findings.card.delete")}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("findings.card.deleteConfirm")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">{finding.title}</p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          variant="danger"
          loading={deleteMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
